import * as React from 'react';
import { Slider } from '@/hen-ui/composites/ImageGrid/variants/Slider';
import { Grid } from '@/hen-ui/composites/ImageGrid/variants/Grid';
import { GridImage } from '@/hen-ui/composites/GridImage';
import { SliderImage } from '@/hen-ui/composites/SliderImage';

export type ImageGridProps = React.PropsWithChildren<{
  json?: {
    url: string;
    alt?: string;
  }[];
  variant?: 'slider' | 'grid';
  width?: number;
  height?: number;
  /** Tailwind gap class for spacing between items. Default: 'gap-max'. */
  gap?: string;
}>;

/**
 * ImageGrid - A composite component for displaying images in a grid or slider layout
 *
 * Supports two ways of providing images:
 * 1. Via `json` prop (array of image objects) - convenience wrapper
 * 2. Via `children` prop (GridImage or SliderImage components)
 *
 * The `json` prop is converted to children internally, so there's only one rendering path.
 *
 * @example
 * ```tsx
 * // Using json prop (convenience wrapper)
 * <ImageGrid
 *   variant="grid"
 *   json={[
 *     { url: '/image1.jpg', alt: 'Image 1' },
 *     { url: '/image2.jpg', alt: 'Image 2' }
 *   ]}
 * />
 *
 * // Using children
 * <ImageGrid variant="grid">
 *   <GridImage src="/image1.jpg" alt="Image 1" />
 *   <GridImage src="/image2.jpg" alt="Image 2" />
 * </ImageGrid>
 * ```
 */
export const ImageGrid: React.FC<ImageGridProps> = ({
  variant = 'slider',
  json,
  width = 215,
  height = 100,
  gap,
  children,
}) => {
  // Convert json to children if provided
  let renderedChildren = children;
  if (json && json.length > 0) {
    const ImageComponent = variant === 'slider' ? SliderImage : GridImage;
    renderedChildren = json.map((image, index) => (
      <ImageComponent
        key={index}
        src={image.url}
        alt={image.alt || `Image ${index + 1}`}
        width={width}
        height={height}
      />
    ));
  }

  const Comp = variant === 'slider' ? Slider : Grid;
  return (
    <div className='place-items-center mx-auto'>
      <Comp width={width} height={height} gap={gap}>
        {renderedChildren}
      </Comp>
    </div>
  );
};

ImageGrid.displayName = 'ImageGrid';
